// src/utils/geo.js

/**
 * Geo helpers for distance/bearing and angle math.
 * All angles are in degrees unless otherwise noted.
 */

export function clampNumber(value, min, max) {
  const v = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return Math.min(max, Math.max(min, v));
}

export function normalizeDegrees(deg) {
  const n = typeof deg === "number" && Number.isFinite(deg) ? deg : 0;
  // normalize into [0, 360)
  return ((n % 360) + 360) % 360;
}

export function shortestSignedAngleDeltaDegrees(fromDeg, toDeg) {
  const from = normalizeDegrees(fromDeg);
  const to = normalizeDegrees(toDeg);
  let delta = to - from;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

export function degreesToRadians(deg) {
  return (deg * Math.PI) / 180;
}

export function radiansToDegrees(rad) {
  return (rad * 180) / Math.PI;
}

export function isValidLatLng(lat, lng) {
  return (
    typeof lat === "number" &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === "number" &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  );
}

function requestCurrentPosition(options) {
  if (
    typeof navigator === "undefined" ||
    !navigator.geolocation ||
    typeof navigator.geolocation.getCurrentPosition !== "function"
  ) {
    return Promise.reject(new Error("Geolocation not supported"));
  }

  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 0,
  } = options || {};

  const geoOptions = {
    enableHighAccuracy: Boolean(enableHighAccuracy),
    timeout: Math.max(0, Number(timeout) || 0),
    maximumAge: Math.max(0, Number(maximumAge) || 0),
  };

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = position?.coords;
        const lat = coords?.latitude;
        const lng = coords?.longitude;

        if (!isValidLatLng(lat, lng)) {
          reject(new Error("Invalid geolocation coordinates"));
          return;
        }

        resolve({
          lat,
          lng,
          accuracy:
            typeof coords?.accuracy === "number" ? coords.accuracy : null,
          altitude:
            typeof coords?.altitude === "number" ? coords.altitude : null,
          altitudeAccuracy:
            typeof coords?.altitudeAccuracy === "number"
              ? coords.altitudeAccuracy
              : null,
          heading:
            typeof coords?.heading === "number" ? coords.heading : null,
          speed: typeof coords?.speed === "number" ? coords.speed : null,
          timestamp:
            typeof position?.timestamp === "number"
              ? position.timestamp
              : Date.now(),
        });
      },
      (error) => {
        reject(error || new Error("Unable to get location"));
      },
      geoOptions
    );
  });
}

/**
 * Request a fresh device location fix.
 * Defaults to maximumAge=0 so recenter actions do not reuse stale coordinates.
 */
export function getFreshCurrentPosition(options) {
  return requestCurrentPosition({
    ...options,
    maximumAge:
      options && Object.prototype.hasOwnProperty.call(options, "maximumAge")
        ? options.maximumAge
        : 0,
  });
}

/**
 * Prefer a fresh fix, but fall back to a recent cached fix if the fresh lookup
 * times out or the device cannot provide a new sample quickly.
 */
export async function getBestAvailableCurrentPosition(options) {
  const {
    enableHighAccuracy = true,
    timeout = 10000,
    fallbackMaximumAge = 120000,
    fallbackTimeout = 4000,
  } = options || {};

  try {
    return await getFreshCurrentPosition({
      enableHighAccuracy,
      timeout,
      maximumAge: 0,
    });
  } catch (error) {
    if (error?.code === 1) {
      throw error;
    }

    return requestCurrentPosition({
      enableHighAccuracy,
      timeout: fallbackTimeout,
      maximumAge: fallbackMaximumAge,
    });
  }
}

/**
 * Haversine distance in meters between two lat/lng points.
 */
export function haversineDistanceMeters(a, b) {
  if (
    !a ||
    !b ||
    !isValidLatLng(a.latitude, a.longitude) ||
    !isValidLatLng(b.latitude, b.longitude)
  ) {
    return null;
  }

  const R = 6371000; // meters
  const lat1 = degreesToRadians(a.latitude);
  const lat2 = degreesToRadians(b.latitude);
  const dLat = degreesToRadians(b.latitude - a.latitude);
  const dLon = degreesToRadians(b.longitude - a.longitude);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);

  const aa =
    sinDLat * sinDLat +
    Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;

  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  const d = R * c;

  return Number.isFinite(d) ? d : null;
}

/**
 * Bearing (forward azimuth) from point a to point b in degrees [0, 360).
 * 0 = North, 90 = East.
 */
export function bearingDegrees(a, b) {
  if (
    !a ||
    !b ||
    !isValidLatLng(a.latitude, a.longitude) ||
    !isValidLatLng(b.latitude, b.longitude)
  ) {
    return null;
  }

  const lat1 = degreesToRadians(a.latitude);
  const lat2 = degreesToRadians(b.latitude);
  const dLon = degreesToRadians(b.longitude - a.longitude);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  const brng = radiansToDegrees(Math.atan2(y, x));
  return normalizeDegrees(brng);
}

/**
 * Map distance (meters) to a scale factor with clamps.
 * The output is in [minScale, maxScale].
 *
 * - At or beyond farMeters, returns minScale.
 * - At or below nearMeters, returns maxScale.
 * - Linear interpolation in between.
 */
export function scaleFromDistanceMeters(distanceMeters, options) {
  const {
    nearMeters = 10,
    farMeters = 250,
    minScale = 0.6,
    maxScale = 2.0,
  } = options || {};

  if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters)) {
    return minScale;
  }

  const near = Math.max(0.1, nearMeters);
  const far = Math.max(near + 0.1, farMeters);

  if (distanceMeters <= near) return maxScale;
  if (distanceMeters >= far) return minScale;

  const t = (distanceMeters - near) / (far - near); // 0..1
  // t=0 => maxScale, t=1 => minScale
  const s = maxScale + (minScale - maxScale) * t;
  return clampNumber(s, minScale, maxScale);
}

/**
 * Convenience: given heading (device points), and target bearing,
 * returns signed delta degrees in [-180, 180] where:
 * - negative means target is to the LEFT
 * - positive means target is to the RIGHT
 */
export function directionDeltaDegrees(headingDeg, targetBearingDeg) {
  if (
    typeof headingDeg !== "number" ||
    !Number.isFinite(headingDeg) ||
    typeof targetBearingDeg !== "number" ||
    !Number.isFinite(targetBearingDeg)
  ) {
    return null;
  }
  return shortestSignedAngleDeltaDegrees(headingDeg, targetBearingDeg);
}
